/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Divider,
  List,
  Typography,
  Tooltip,
  Skeleton,
  Select,
  Form,
  notification,
  Modal,
  Button,
  Input,
  Radio,
} from 'antd';
import {
  EyeOutlined,
  SearchOutlined,
  CloseOutlined,
  CheckOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import ShipmentLogs from './ShipmentLogs';
import {
  SHIPMENT_PRIORITY_OPTIONS,
  SHIPMENT_CATEGORY_OPTIONS,
  SHIPMENT_PACKAGING_OPTIONS,
  normalizeShipmentMetaFromRecord,
  packagingFormValueToApi,
  getPopupContainerBody,
} from './shipmentFieldOptions';
import {
  OnDeleteShipmentNumber,
  OnFetchShipmentSno,
  OnAddShipmentNumber,
  OnPreviewShipmentNumber,
  OnAddorUpdateShipmentNumber,
} from '../../redux/ShipmentDetails/saga';

const { Text } = Typography;
const { Option } = Select;

const ShipmentNo = (props) => {
  const { selectedData } = props;

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchSno, setSearchSno] = useState(false);
  const [equipSnoDrop, setEquipSnoDrop] = useState([]);
  const [equipSnoList, setEquipSnoList] = useState([]);
  const [equipSnoSearchForm] = Form.useForm();
  const [isTableModelVisible, setIsTableModelVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedDataSno, setSelectedDataSno] = useState('');
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [previewShipmentNumber, setPreviewShipmentNumber] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editShipmentNumber, setEditShipmentNumber] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editPriority, setEditPriority] = useState('medium');
  const [editCategory, setEditCategory] = useState('project');
  const [editPackaging, setEditPackaging] = useState(undefined);

  const [confirmPriority, setConfirmPriority] = useState('medium');
  const [confirmCategory, setConfirmCategory] = useState('project');
  const [confirmPackaging, setConfirmPackaging] = useState(undefined);

  useEffect(() => {
    if (selectedData) OnfetchData();
  }, [selectedData]);

  useEffect(() => {
    if (selectedData) {
      const fetchShippingForms = async () => {
        try {
          setFetchLoading(true);
          await OnfetchData();
          setFetchLoading(false);
        } catch (error) {
          console.error('Failed to fetch shipping forms:', error);
          setFetchLoading(false);
        }
      };

      fetchShippingForms();
    }
  }, [selectedData]);

  const handleEyeClick = async (record) => {
    setSelectedDataSno(record);
    setIsTableModelVisible(true);
  };

  const OnfetchData = async () => {
    const obj = {
      id: selectedData.id,
    };
    const res = await OnFetchShipmentSno(obj);
    if (res['shipmentType']?.shipments && res['shipmentType'].shipments.length > 0) {
      setEquipSnoList(res['shipmentType'].shipments);
      setEquipSnoDrop(res['shipmentType'].shipments);
    }
  };

  const showDeleteModal = (record) => {
    setSelectedDataSno(record);
    setIsDeleteModalVisible(true);
  };

  const openEditShipmentNumber = (record) => {
    setSelectedDataSno(record);
    setEditShipmentNumber(record.shipmentNumber || '');
    const meta = normalizeShipmentMetaFromRecord(record);
    setEditPriority(meta.priority);
    setEditCategory(meta.shipmentCategory);
    setEditPackaging(meta.packagingSubcategory);
    setIsEditModalVisible(true);
  };

  const handleEditOk = async () => {
    const trimmed = String(editShipmentNumber || '').trim();
    if (!trimmed) {
      notification.warning({ message: 'Please enter a shipment number.' });
      return;
    }
    if (!editPackaging) {
      notification.warning({ message: 'Please select packaging (subcategory).' });
      return;
    }
    setEditLoading(true);
    const obj = {
      id: selectedDataSno.id,
      data: {
        shipment: {
          shipmentTypeId: selectedData.id,
          shipmentNumber: trimmed,
          priority: editPriority,
          shipmentCategory: editCategory,
          packagingType: packagingFormValueToApi(editPackaging),
        },
      },
    };
    const res = await OnAddorUpdateShipmentNumber(obj, true);
    if (res) await OnfetchData();
    setEditLoading(false);
    setIsEditModalVisible(false);
    setSelectedDataSno('');
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setSelectedDataSno('');
    setEditShipmentNumber('');
    setEditPackaging(undefined);
  };

  const handleOk = async () => {
    setDeleteLoading(true);
    await OnDeleteShipmentNumber(selectedDataSno);
    await OnfetchData();
    setDeleteLoading(false);
    setIsDeleteModalVisible(false);
  };

  const handleCancel = () => {
    setIsDeleteModalVisible(false);
    setSelectedDataSno('');
  };

  // Step 1: Initial click to preview shipment number
  const handleAddButtonClick = async () => {
    if (!selectedData || !selectedData.id) {
      notification.error({
        message: 'Cannot add shipment number',
        description: 'No shipment type selected',
      });
      return;
    }

    setAddLoading(true);

    try {
      // Call the preview endpoint to get the next number
      const previewData = await OnPreviewShipmentNumber(selectedData.id);

      if (previewData && previewData.previewShipmentNumber) {
        setPreviewShipmentNumber(previewData.previewShipmentNumber);
        setConfirmPriority('medium');
        setConfirmCategory('project');
        setConfirmPackaging(undefined);
        setIsConfirmModalVisible(true);
      } else {
        notification.error({
          message: 'Failed to generate shipment number preview',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error generating shipment number',
        description: error?.message || 'Unknown error',
      });
    } finally {
      setAddLoading(false);
    }
  };

  // Step 2: Handle confirmation (Yes/No)
  const handleConfirmAdd = async () => {
    if (!confirmPackaging) {
      notification.warning({ message: 'Please select packaging (subcategory).' });
      return;
    }
    const num = String(previewShipmentNumber || '').trim();
    if (!num) {
      notification.warning({ message: 'Please enter a shipment number.' });
      return;
    }
    setAddLoading(true);

    const payload = {
      shipment: {
        shipmentTypeId: selectedData.id,
        shipmentNumber: num,
        priority: confirmPriority,
        shipmentCategory: confirmCategory,
        packagingType: packagingFormValueToApi(confirmPackaging),
      },
    };

    const response = await OnAddShipmentNumber(payload);

    if (response?.shipment?.shipmentNumber) {
      notification.success({
        message: `Shipment number ${response.shipment.shipmentNumber} added successfully`,
      });
    }

    // Close the modal
    setIsConfirmModalVisible(false);

    await OnfetchData();
    setAddLoading(false);
  };

  const handleCancelAdd = () => {
    setIsConfirmModalVisible(false);
    setPreviewShipmentNumber('');
    setConfirmPackaging(undefined);
  };

  const onSelectEquipSno = () => {
    setSearchSno(true);
  };

  const onSearchEquipmentSno = () => {
    equipSnoSearchForm.validateFields().then(async (data) => {
      if (data && data.shipmentNumber) {
        setSearchSno(false);
        const newArray = equipSnoDrop.find((u) => u.shipmentNumber === data.shipmentNumber);
        setEquipSnoList([newArray]);
      } else {
        notification.warning({
          message: 'Select Equipment Sno to Search.',
        });
      }
    });
  };

  return (
    <div className="equipmentsSno">
      <div className="search_equipSno">
        {!searchOpen ? (
          <Tooltip title={'Search Shipment Number'}>
            <SearchOutlined
              className="searchIcon"
              onClick={() => setSearchOpen(true)}
            />
          </Tooltip>
        ) : (
          <div className="searchDiv">
            <Form form={equipSnoSearchForm}>
              <Form.Item
                name="shipmentNumber"
                required
              >
                <Select
                  className="client-select search_equip"
                  placeholder="Search shipment number"
                  showSearch
                  optionFilterProp="children"
                  onChange={onSelectEquipSno}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {equipSnoDrop &&
                    equipSnoDrop.map((item, i) => (
                      <Option
                        key={i}
                        value={item && item.shipmentNumber}
                      >
                        {item && item.shipmentNumber}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Form>
            {searchSno && (
              <Tooltip title={'Search'}>
                <CheckOutlined
                  className="rightIcon"
                  onClick={onSearchEquipmentSno}
                />
              </Tooltip>
            )}
            <Tooltip title={'Clear Search'}>
              <CloseOutlined
                className="closeIcon"
                onClick={() => {
                  setSearchOpen(false);
                  equipSnoSearchForm.resetFields();
                  setEquipSnoList(equipSnoDrop);
                }}
              />
            </Tooltip>
          </div>
        )}
      </div>
      <Divider
        orientation="left"
        className="divider"
        style={{ marginTop: searchOpen ? '4px' : '16px' }}
      >
        <Tooltip
          placement="top"
          title="Add New Shipment Number"
        >
          <PlusOutlined
            className="addNewSerialNo"
            onClick={handleAddButtonClick}
          />
        </Tooltip>
        <Text>Shipments</Text>
      </Divider>

      <Skeleton
        loading={fetchLoading}
        active
      >
        <List
          className="checksList-equip"
          size="large"
          bordered
          dataSource={equipSnoList}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Tooltip
                  key={'view'}
                  title={`View ${item.shipmentNumber} History`}
                  placement="right"
                  onClick={() => handleEyeClick(item)}
                >
                  <EyeOutlined
                    className="sno_icon"
                    key={'view'}
                  />
                </Tooltip>,
                <Tooltip
                  key={'edit'}
                  title="Edit shipment number"
                  placement="right"
                >
                  <EditOutlined
                    className="sno_icon"
                    key={'edit'}
                    onClick={() => openEditShipmentNumber(item)}
                  />
                </Tooltip>,
                <DeleteOutlined
                  className="sno_icon"
                  key={'delete'}
                  onClick={() => {
                    showDeleteModal(item);
                  }}
                />,
              ]}
            >
              <Text>{item.shipmentNumber}</Text>
            </List.Item>
          )}
          loading={fetchLoading}
        />
      </Skeleton>

      {isTableModelVisible && (
        <ShipmentLogs
          showFormModel={isTableModelVisible}
          setShowFormModel={setIsTableModelVisible}
          selectedData={selectedDataSno}
        />
      )}

      <Modal
        width={560}
        className="pipeline-modal shipment-add-modal"
        title="Confirm New Shipment Number"
        centered
        visible={isConfirmModalVisible}
        destroyOnClose
        bodyStyle={{
          maxHeight: 'calc(100vh - 220px)',
          overflowY: 'auto',
          padding: '16px 24px',
        }}
        footer={[
          <Button
            key="cancel"
            onClick={handleCancelAdd}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleConfirmAdd}
            loading={addLoading}
            disabled={!confirmPackaging || !String(previewShipmentNumber || '').trim()}
          >
            Confirm
          </Button>,
        ]}
      >
        <div>
          <Form layout="vertical">
            <Form.Item
              label="Priority"
              required
            >
              <Select
                className="client-select"
                value={confirmPriority}
                onChange={setConfirmPriority}
                getPopupContainer={getPopupContainerBody}
              >
                {SHIPMENT_PRIORITY_OPTIONS.map((o) => (
                  <Option
                    key={o.value}
                    value={o.value}
                  >
                    {o.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Category"
              required
            >
              <Radio.Group
                className="shipment-category-radio-group"
                value={confirmCategory}
                onChange={(e) => setConfirmCategory(e.target.value)}
              >
                {SHIPMENT_CATEGORY_OPTIONS.map((o) => (
                  <Radio
                    key={o.value}
                    value={o.value}
                  >
                    {o.label}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
            <Form.Item
              label="Subcategory (packaging)"
              required
            >
              <Select
                className="client-select"
                placeholder="Select packaging type"
                allowClear
                value={confirmPackaging}
                onChange={setConfirmPackaging}
                getPopupContainer={getPopupContainerBody}
              >
                {SHIPMENT_PACKAGING_OPTIONS.map((o) => (
                  <Option
                    key={o.value}
                    value={o.value}
                  >
                    {o.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Shipment number"
              required
            >
              <Input
                size="large"
                value={previewShipmentNumber}
                onChange={(e) => setPreviewShipmentNumber(e.target.value)}
                style={{ fontWeight: 700, color: '#EF314C' }}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      <Modal
        width={560}
        className="pipeline-modal shipment-add-modal"
        title="Edit Shipment Number"
        centered
        visible={isEditModalVisible}
        destroyOnClose
        bodyStyle={{
          maxHeight: 'calc(100vh - 220px)',
          overflowY: 'auto',
          padding: '16px 24px',
        }}
        footer={[
          <Button
            key="cancel"
            onClick={handleEditCancel}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleEditOk}
            loading={editLoading}
            disabled={!editPackaging}
          >
            Save
          </Button>,
        ]}
      >
        <Form layout="vertical">
          <Form.Item
            label="Priority"
            required
          >
            <Select
              className="client-select"
              value={editPriority}
              onChange={setEditPriority}
              getPopupContainer={getPopupContainerBody}
            >
              {SHIPMENT_PRIORITY_OPTIONS.map((o) => (
                <Option
                  key={o.value}
                  value={o.value}
                >
                  {o.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Category"
            required
          >
            <Radio.Group
              className="shipment-category-radio-group"
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
            >
              {SHIPMENT_CATEGORY_OPTIONS.map((o) => (
                <Radio
                  key={o.value}
                  value={o.value}
                >
                  {o.label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="Subcategory (packaging)"
            required
          >
            <Select
              className="client-select"
              placeholder="Select packaging type"
              allowClear
              value={editPackaging}
              onChange={setEditPackaging}
              getPopupContainer={getPopupContainerBody}
            >
              {SHIPMENT_PACKAGING_OPTIONS.map((o) => (
                <Option
                  key={o.value}
                  value={o.value}
                >
                  {o.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Shipment number"
            required
          >
            <Input
              size="large"
              value={editShipmentNumber}
              onChange={(e) => setEditShipmentNumber(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        width="45vw"
        className="pipeline-modal"
        title="Delete Shipment Number"
        centered
        visible={isDeleteModalVisible}
        footer={[
          <Button
            key="cancel"
            onClick={handleCancel}
          >
            Cancel
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={handleOk}
            loading={deleteLoading}
          >
            Delete
          </Button>,
        ]}
      >
        <div>
          <Text style={{ fontSize: '16px' }}>
            <p>
              Deleting shipment number will delete all the corresponding Stations and related
              information.
            </p>
            Are you sure you want to delete{' '}
            <b style={{ color: '#EF314C' }}>{selectedDataSno && selectedDataSno.shipmentNumber}</b>{' '}
            shipment number?
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default ShipmentNo;
